import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dbSurgeries, type ToothRecord } from '../../lib/localDB';
import { pricingDB, type PricingConfig } from '../../lib/pricingDB';
import { cariDB } from '../../lib/cariDB';
import ConfirmModal from '../../components/ui/ConfirmModal';
import Odontogram from '../new-record/components/Odontogram';
import ToothPanel from '../new-record/components/ToothPanel';
import {
  EMPTY_TOOTH, INITIAL_PATIENT_DATA,
  isToothComplete, buildVDK, isPatientComplete, canSaveRecord,
  PatientFormCard,
  type ToothData, type PatientFormData,
} from '../../shared/record';

export default function EditRecord() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const initialSnapshotRef = useRef('');

  const [loadingData, setLoadingData] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const [patientData, setPatientData] = useState<PatientFormData>({ ...INITIAL_PATIENT_DATA });
  const [teethData, setTeethData] = useState<Record<number, ToothData>>({});
  const [selectedTeeth, setSelectedTeeth] = useState<number[]>([]);
  const [discountType, setDiscountType] = useState<'percent' | 'amount'>('percent');
  const [discountValue, setDiscountValue] = useState<string>('');
  const [pricing, setPricing] = useState<PricingConfig>(() => pricingDB.get());

  useEffect(() => {
    if (!id) { setNotFound(true); setLoadingData(false); return; }
    const load = async () => {
      try {
        const [freshPricing, surgery] = await Promise.all([
          pricingDB.load(),
          dbSurgeries.getById(id),
        ]);
        setPricing(freshPricing);
        if (!surgery) { setNotFound(true); return; }

        setPatientData({
          operation_date: surgery.operation_date,
          patient_name: surgery.patient_name,
          phone: surgery.phone || '',
          clinic: surgery.clinic || '',
          cinsiyet: surgery.cinsiyet,
          yas: surgery.yas,
          seker: surgery.seker,
          sigara: surgery.sigara,
        });

        const teeth: Record<number, ToothData> = {};
        let initialTotalFee = 0;
        surgery.teeth.forEach((t) => {
          teeth[t.tooth_number] = {
            kemik: t.kemik,
            soket: t.soket,
            greft: t.greft,
            hacim: t.hacim,
            healing: (t as any).healing || '',
            implant_brand: t.implant_brand || '',
          };
          initialTotalFee += pricingDB.calcToothFee(freshPricing, { soket: t.soket, greft: t.greft, kemik: t.kemik, hacim: t.hacim, healing: (t as any).healing || '' });
        });
        setTeethData(teeth);

        // Restore discount from saved cari transaction
        const existingTx = (await cariDB.getSurgeryTransactions(id))
          .filter((tx) => tx.type === 'borc');
        const net = existingTx.length > 0 ? existingTx[0].amount : initialTotalFee;
        if (existingTx.length > 0) {
          if (net < initialTotalFee) {
            setDiscountType('amount');
            setDiscountValue((initialTotalFee - net).toString());
          }
        }

        initialSnapshotRef.current = JSON.stringify({
          patientData: {
            operation_date: surgery.operation_date,
            patient_name: surgery.patient_name,
            phone: surgery.phone || '',
            clinic: surgery.clinic || '',
            cinsiyet: surgery.cinsiyet,
            yas: surgery.yas,
            seker: surgery.seker,
            sigara: surgery.sigara,
          },
          teeth: Object.entries(teeth)
            .map(([toothNumber, tooth]) => ({
              toothNumber: Number(toothNumber),
              ...tooth,
            }))
            .sort((a, b) => a.toothNumber - b.toothNumber),
          pricingSnapshot: {
            hasDiscount: net < initialTotalFee,
            netFee: Math.round(net * 100) / 100,
          },
        });
      } catch {
        setNotFound(true);
      } finally {
        setLoadingData(false);
      }
    };
    load();
  }, [id]);

  const configuredTeeth = Object.keys(teethData)
    .map(Number)
    .filter((n) => isToothComplete(teethData[n]));

  const totalFee = configuredTeeth.reduce((sum, n) => {
    const t = teethData[n];
    return sum + pricingDB.calcToothFee(pricing, { soket: t.soket, greft: t.greft, kemik: t.kemik, hacim: t.hacim, healing: t.healing });
  }, 0);

  const discountNum = parseFloat(discountValue) || 0;
  const discountAmount = discountType === 'percent'
    ? Math.round((totalFee * Math.min(discountNum, 100)) / 100)
    : Math.min(discountNum, totalFee);
  const netFee = Math.max(totalFee - discountAmount, 0);
  const hasDiscount = discountAmount > 0;
  const normalizedNetFee = Math.round(netFee * 100) / 100;

  const currentSnapshot = useMemo(() => {
    const orderedTeeth = Object.entries(teethData)
      .map(([toothNumber, tooth]) => ({
        toothNumber: Number(toothNumber),
        ...tooth,
      }))
      .sort((a, b) => a.toothNumber - b.toothNumber);

    return JSON.stringify({
      patientData,
      teeth: orderedTeeth,
      pricingSnapshot: {
        hasDiscount,
        netFee: normalizedNetFee,
      },
    });
  }, [patientData, teethData, hasDiscount, normalizedNetFee]);

  const hasUnsavedChanges = !loadingData
    && initialSnapshotRef.current !== ''
    && currentSnapshot !== initialSnapshotRef.current
    && !success;

  const patientComplete = isPatientComplete(patientData);
  const canSave = canSaveRecord(patientData, configuredTeeth.length);

  const handlePatientChange = (key: keyof PatientFormData, value: string) => {
    setPatientData((prev) => ({ ...prev, [key]: value }));
  };

  const handleToothClick = (n: number) => {
    setSelectedTeeth((prev) => {
      if (prev.includes(n)) {
        return prev.filter((t) => t !== n);
      }
      return [...prev, n];
    });
    if (!teethData[n]) {
      setTeethData((prev) => ({ ...prev, [n]: { ...EMPTY_TOOTH } }));
    }
  };

  const handleToothChange = (updated: ToothData, teethNums?: number[]) => {
    const targets = teethNums ?? selectedTeeth;
    setTeethData((prev) => {
      const next = { ...prev };
      targets.forEach((n) => {
        next[n] = updated;
      });
      return next;
    });
  };

  const handleToothRemove = (n?: number) => {
    if (n !== undefined) {
      // Allow single remove from list view
      setTeethData((prev) => {
        const next = { ...prev };
        delete next[n];
        return next;
      });
      setSelectedTeeth((prev) => prev.filter((t) => t !== n));
    } else {
      // Complete removal from ToothPanel
      setTeethData((prev) => {
        const next = { ...prev };
        selectedTeeth.forEach((t) => delete next[t]);
        return next;
      });
      setSelectedTeeth([]);
    }
  };

  const handleUpdate = async () => {
    if (!canSave || !id) return;
    setSaving(true);
    setError('');
    try {
      const teeth: ToothRecord[] = configuredTeeth.map((n) => {
        const t = teethData[n];
        return {
          tooth_number: n,
          kemik: t.kemik as '1' | '2' | '3',
          soket: t.soket as 'e' | 'h',
          greft: t.greft as 'e' | 'h',
          hacim: t.hacim as 'z' | 'y',
          healing: t.healing as 'e' | 'h',
          implant_brand: t.implant_brand,
          vdk: buildVDK(patientData, t),
        };
      });

      await dbSurgeries.update(id, {
        patient_name: patientData.patient_name.trim(),
        phone: patientData.phone.trim(),
        clinic: patientData.clinic.trim() || undefined,
        operation_date: patientData.operation_date,
        cinsiyet: patientData.cinsiyet as 'k' | 'e',
        yas: patientData.yas as '1' | '2' | '3' | '4',
        seker: patientData.seker as '0' | '1' | '2',
        sigara: patientData.sigara as '0' | '1' | '2',
        teeth,
      });

      // Her zaman cari kaydını güncelle — netFee=0 (tam indirim) olsa bile
      await cariDB.upsertBySurgery(id, {
        clinic_name: patientData.clinic.trim() || '-',
        type: 'borc',
        amount: netFee,
        date: patientData.operation_date,
        description: `Ameliyat — ${patientData.patient_name.trim()} (${configuredTeeth.length} diş)${discountAmount > 0 ? ` · ${pricing.currency}${discountAmount.toLocaleString('tr-TR')} indirim` : ''}`,
        surgery_id: id,
      });

      initialSnapshotRef.current = currentSnapshot;
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Güncelleme sırasında bir hata oluştu';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const activeTooth = selectedTeeth.length > 0 ? (teethData[selectedTeeth[0]] ?? { ...EMPTY_TOOTH }) : null;

  const handleNavigateBack = () => {
    if (hasUnsavedChanges && !saving) {
      setShowLeaveConfirm(true);
      return;
    }
    navigate('/dashboard');
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges || saving || success) return;
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, saving, success]);

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Kayıt yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl p-10">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-damage-line text-3xl text-red-400"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Kayıt bulunamadı</h2>
          <p className="text-gray-500 text-sm mb-6">Bu kayıt silinmiş veya mevcut değil.</p>
          <button onClick={() => navigate('/dashboard')}
            className="px-6 py-2.5 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-all cursor-pointer whitespace-nowrap text-sm">
            Dashboard&apos;a Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
              <i className="ri-edit-line text-lg text-white"></i>
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">Kayıt Düzenle</h1>
              <p className="text-xs text-gray-500">{patientData.patient_name || 'Hasta'}</p>
            </div>
          </div>
          <button type="button" onClick={handleNavigateBack}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm">
            <i className="ri-arrow-left-line"></i>Geri
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm flex items-center gap-2">
            <i className="ri-check-circle-line text-lg"></i>
            Kayıt güncellendi! Dashboard&apos;a yönlendiriliyorsunuz...
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-2 space-y-5">
            <PatientFormCard
              patientData={patientData}
              onPatientChange={handlePatientChange}
              accentColor="amber"
              headerExtra={
                <span className="ml-auto px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                  Düzenleme Modu
                </span>
              }
            />

            <Odontogram teethData={teethData} selectedTeeth={selectedTeeth} onToothClick={handleToothClick} />

            {/* Odontogram moved Summary from here to right column */}
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-5">
            {selectedTeeth.length > 0 && activeTooth !== null ? (() => {
              const snapshotTeeth = [...selectedTeeth];
              return (
                <ToothPanel
                  toothNumbers={snapshotTeeth}
                  data={activeTooth}
                  patientData={patientData}
                  onChange={(updated) => handleToothChange(updated, snapshotTeeth)}
                  onRemove={() => handleToothRemove()}
                  onClose={() => setSelectedTeeth([])}
                />
              );
            })() : (
              <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-8 flex flex-col items-center justify-center text-center min-h-[200px]">
                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                  <i className="ri-cursor-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-sm font-semibold text-gray-600 mb-1">Diş seçilmedi</p>
                <p className="text-xs text-gray-400">Odontogramdan bir dişe tıklayarak düzenleyebilirsiniz</p>
              </div>
            )}

            {/* CONFIGURED TEETH SUMMARY (MOVED HERE) */}
            {configuredTeeth.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 bg-teal-500 text-white rounded-full text-xs flex items-center justify-center font-bold">
                    {configuredTeeth.length}
                  </span>
                  Girilen Dişler
                </h3>
                <div className="space-y-2">
                  {configuredTeeth.sort((a, b) => a - b).map((n) => {
                    const t = teethData[n];
                    const vdk = buildVDK(patientData, t);
                    const fee = pricingDB.calcToothFee(pricing, { soket: t.soket, greft: t.greft, kemik: t.kemik, hacim: t.hacim, healing: t.healing });
                    return (
                      <div key={n} className="flex items-center justify-between py-2.5 px-4 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors cursor-pointer" onClick={() => handleToothClick(n)}>
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 bg-amber-500 text-white rounded-lg text-xs font-bold flex items-center justify-center">{n}</span>
                          <div className="flex gap-1.5 text-xs flex-wrap">
                            {[
                              { label: 'Kemik', val: t.kemik },
                              { label: 'Soket', val: t.soket },
                              { label: 'Greft', val: t.greft },
                              { label: 'Hacim', val: t.hacim },
                              { label: 'Healing', val: t.healing },
                            ].map((item) => (
                              <span key={item.label} className="px-2 py-0.5 bg-white text-amber-700 rounded font-medium border border-amber-200">
                                {item.label}: {item.val || '—'}
                              </span>
                            ))}
                            {t.implant_brand && (
                              <span className="px-2 py-0.5 bg-amber-500 text-white rounded font-medium flex items-center gap-1">
                                <i className="ri-award-line" style={{ fontSize: '10px' }}></i>
                                {t.implant_brand}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-sm font-bold text-amber-700 tracking-wider">{vdk}</span>
                          <span className="text-sm font-bold text-emerald-600 whitespace-nowrap">
                            {pricing.currency}{fee.toLocaleString('tr-TR')}
                          </span>
                          <button type="button" onClick={(e) => { e.stopPropagation(); handleToothRemove(n); }}
                            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Total + Discount + Net */}
                <div className="mt-3 pt-3 border-t border-amber-100 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-600 flex items-center gap-1.5">
                      <i className="ri-coins-line text-amber-500"></i>
                      Toplam ({configuredTeeth.length} diş)
                    </span>
                    <span className={`text-base font-bold ${discountAmount > 0 ? 'text-gray-400 line-through text-sm' : 'text-amber-700'}`}>
                      {pricing.currency}{totalFee.toLocaleString('tr-TR')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-600 flex items-center gap-1.5 flex-shrink-0">
                      <i className="ri-percent-line text-orange-400"></i>İndirim
                    </span>
                    <div className="flex-1 flex items-center gap-1.5">
                      <div className="flex bg-gray-100 rounded-lg p-0.5">
                        <button type="button" onClick={() => { setDiscountType('percent'); setDiscountValue(''); }}
                          className={`px-2.5 py-1 rounded-md text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${discountType === 'percent' ? 'bg-orange-400 text-white' : 'text-gray-500 hover:text-gray-700'}`}>%</button>
                        <button type="button" onClick={() => { setDiscountType('amount'); setDiscountValue(''); }}
                          className={`px-2.5 py-1 rounded-md text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${discountType === 'amount' ? 'bg-orange-400 text-white' : 'text-gray-500 hover:text-gray-700'}`}>{pricing.currency}</button>
                      </div>
                      <input type="number" min="0" max={discountType === 'percent' ? 100 : totalFee} value={discountValue}
                        onChange={(e) => setDiscountValue(e.target.value)}
                        placeholder={discountType === 'percent' ? 'örn: 10' : 'örn: 500'}
                        className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-300 focus:border-transparent transition-all" />
                      {discountValue && (
                        <button type="button" onClick={() => setDiscountValue('')}
                          className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 flex-shrink-0 cursor-pointer">
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      )}
                    </div>
                    {discountAmount > 0 && (
                      <span className="text-sm font-bold text-orange-500 whitespace-nowrap flex-shrink-0">
                        -{pricing.currency}{discountAmount.toLocaleString('tr-TR')}
                      </span>
                    )}
                  </div>
                  <div className={`flex items-center justify-between pt-2 border-t ${discountAmount > 0 ? 'border-orange-100' : 'border-amber-100'}`}>
                    <span className={`text-sm font-bold flex items-center gap-1.5 ${discountAmount > 0 ? 'text-orange-600' : 'text-amber-700'}`}>
                      <i className={`${discountAmount > 0 ? 'ri-shield-check-line text-orange-400' : 'ri-coins-line text-amber-500'}`}></i>
                      {discountAmount > 0 ? 'Net Fiyat' : 'Toplam'}
                    </span>
                    <span className={`text-xl font-bold ${discountAmount > 0 ? 'text-orange-600' : 'text-amber-700'}`}>
                      {pricing.currency}{netFee.toLocaleString('tr-TR')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* UPDATE CARD */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
              <h3 className="text-sm font-bold text-gray-800">Değişiklikleri Kaydet</h3>
              <div className="space-y-2 text-sm">
                {[
                  { label: 'Hasta adı', ok: patientData.patient_name.trim() !== '' },
                  { label: 'Cinsiyet & Yaş & Şeker & Sigara', ok: patientComplete },
                  { label: 'En az 1 diş girildi', ok: configuredTeeth.length > 0 },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${item.ok ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                      <i className={`${item.ok ? 'ri-check-line' : 'ri-subtract-line'} text-xs`}></i>
                    </span>
                    <span className={item.ok ? 'text-gray-800 font-medium' : 'text-gray-400'}>{item.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={handleNavigateBack}
                  className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer text-sm">İptal</button>
                <button type="button" onClick={handleUpdate} disabled={!canSave || saving}
                  className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all whitespace-nowrap ${canSave && !saving ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 cursor-pointer' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
                  {saving ? 'Güncelleniyor...' : canSave ? `Güncelle (${configuredTeeth.length} diş)` : 'Eksik alan var'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showLeaveConfirm && (
        <ConfirmModal
          title="Kaydedilmemiş değişiklikler var"
          message="İndirim veya diğer düzenlemeler kaydedilmeden çıkarsanız değişiklikler kaybolur. Dashboard'a yine de dönmek istiyor musunuz?"
          confirmLabel="Kaydetmeden Çık"
          variant="danger"
          onConfirm={() => {
            setShowLeaveConfirm(false);
            navigate('/dashboard');
          }}
          onCancel={() => setShowLeaveConfirm(false)}
        />
      )}
    </div>
  );
}
